import React, { useEffect, useState } from 'react';
import { ExternalLink, Facebook, Instagram, Youtube, Linkedin } from 'lucide-react';
import { SocialLinks, getSocialLinksByUserId } from '../services/socialLinksService';

interface SocialLinksListProps {
  userId?: string | null;
  className?: string;
}

// SocialLinksList renders read-only social media links for a given user.
// It is intended for use on public detail pages (trader, craftie, hospitality, professional).
const SocialLinksList: React.FC<SocialLinksListProps> = ({ userId, className }) => {
  const [links, setLinks] = useState<SocialLinks | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!userId) return;
      try {
        const data = await getSocialLinksByUserId(userId);
        setLinks(data);
      } catch (error) {
        console.error('Failed to load public social links:', error);
      }
    };

    load();
  }, [userId]);

  if (!userId || !links) {
    return null;
  }

  if (links.show_socials === false) {
    return null;
  }

  const showFacebook = links.show_facebook !== false;
  const showInstagram = links.show_instagram !== false;
  const showYoutube = links.show_youtube !== false;
  const showLinkedin = links.show_linkedin !== false;
  const showTiktok = links.show_tiktok !== false;

  const items = [
    {
      key: 'facebook_url' as const,
      label: 'Facebook',
      url: links.facebook_url,
      visible: showFacebook,
      Icon: Facebook,
    },
    {
      key: 'instagram_url' as const,
      label: 'Instagram',
      url: links.instagram_url,
      visible: showInstagram,
      Icon: Instagram,
    },
    {
      key: 'youtube_url' as const,
      label: 'YouTube',
      url: links.youtube_url,
      visible: showYoutube,
      Icon: Youtube,
    },
    {
      key: 'linkedin_url' as const,
      label: 'LinkedIn',
      url: links.linkedin_url,
      visible: showLinkedin,
      Icon: Linkedin,
    },
    {
      key: 'tiktok_url' as const,
      label: 'TikTok',
      url: links.tiktok_url,
      visible: showTiktok,
      Icon: ExternalLink,
    },
  ].filter((item) => item.visible && item.url && item.url.trim().length > 0);

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Social media</h3>
      <div className="flex flex-wrap gap-3">
        {items.map((item) => {
          const Icon = item.Icon;
          return (
            <a
              key={item.key}
              href={item.url as string}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
            >
              <Icon className="w-4 h-4" />
              <span>{item.label}</span>
            </a>
          );
        })}
      </div>
    </div>
  );
};

export default SocialLinksList;
